
import { Context } from '@nuxt/types'
import { defineComponent } from 'vue'
import MachinationsLightPage from '~/domain/pages/MachinationsLightPage'
import { DiagramStoreActions } from '@interfaces/index'
import HomepageArticles from '~/components/homepage/HomepageArticles.vue'
import AcquisitionFAQ from '~/components/acquisition/AcquisitionFAQ.vue'
import CommonVideoPlayerDialog from '~/components/common/CommonVideoPlayerDialog.vue'
import CommonTags from '~/components/common/CommonTags.vue'
import { getDiagramUrl } from '~/domain/helpers'
import { getThumbnailUrl } from '@helpers/DiagramHelper'
import ProfileAvatar from '~/components/profile/ProfileAvatar.vue'
import DiagramLikeBtn from '~/components/diagram/DiagramLikeBtn.vue'
import DiagramForkBtn from '~/components/diagram/DiagramForkBtn.vue'
import CommonParagraphsBlock from '~/components/common/CommonParagraphsBlock.vue'
import AppActions from '@constants/AppActions'
import DiagramTags from '~/components/diagram/DiagramTags.vue'
import CommonTextBlock from '~/components/common/CommonTextBlock.vue'
import RelatedGlossaryTerms from '~/components/glossary/RelatedGlossaryTerms.vue'
import { IRelatedGlossaryTerm } from '~/domain/interfaces/ResourcesTypes'
export default defineComponent({
  name: 'GlossaryItem',
  components: {
    DiagramTags,
    CommonTextBlock,
    CommonParagraphsBlock,
    DiagramForkBtn,
    DiagramLikeBtn,
    ProfileAvatar,
    CommonTags,
    CommonVideoPlayerDialog,
    AcquisitionFAQ,
    HomepageArticles,
    RelatedGlossaryTerms,
  },
  extends: MachinationsLightPage,
  layout: 'machinations',
  async asyncData(context: Context): Promise<{
    chapter: unknown[]
    glossaryC: any
    glossaryChapters: any
    diagram: any
    relatedGlossaryTerms: IRelatedGlossaryTerm[]
  }> {
    const { $config, store, params, error, route } = context
    const { wpBaseURL } = $config
    await store.commit('wp/setPageNumber', 1)
    // TODO: no of posts as param, get only 3 for latest news
    await store.dispatch('wp/getPosts', { context, wpBaseURL })

    await store.dispatch('common/setupPageLight', { context })

    await store.dispatch('wp/fetchPageBySlug', {
      context,
      slug: 'glossary',
    })
    const glossaryChapters =
      store.getters['wp/getAcfBlocks'][0].block.chapters[0]
    const glossaryC =
      store.getters['wp/getAcfBlocks'][0].block.chapters[0].block

    const slugs = Object.values(glossaryChapters.chapter).map(
      (chapter: any) => {
        return chapter.title
          .trim()
          .toLowerCase()
          .replace(/ /g, '-')
          .replace(/[^\w-]+/g, '')
      }
    )

    if (!slugs.includes(params.slug)) {
      // @ts-ignore
      return error({
        statusCode: 404,
        message: `Invalid route: ${route.path}`,
      })
    }
    const chapterTest: any = Object.values(glossaryChapters.chapter).filter(
      (chapter: any) => {
        return (
          chapter.title
            .trim()
            .toLowerCase()
            .replace(/ /g, '-')
            .replace(/[^\w-]+/g, '') === params.slug
        )
      }
    )
    const extractedFuid = chapterTest[0].diagramFuid

    await store.dispatch(DiagramStoreActions.GET_GLOSSARY_DIAGRAMS, {
      context,
      params: {
        fileKeys: extractedFuid,
        diagramView: 'true',
      },
    })
    const glossaryDiagrams = store.state.diagrams.glossary

    if (chapterTest[0].diagramFuid) {
      const matchingDiagram = glossaryDiagrams.find(
        (diagram: { fuid: string }) =>
          diagram.fuid === chapterTest[0].diagramFuid
      )
      if (matchingDiagram) {
        chapterTest[0].diagramName = matchingDiagram.name
        chapterTest[0].diagramLink =
          $config.nuxtBaseURL + getDiagramUrl(matchingDiagram)
        chapterTest[0].diagramThumbnailUrl =
          $config.apiBaseURL + getThumbnailUrl(matchingDiagram)
      }
    }

    // this is a more complicated way of getting related glossary terms
    // but it's more accurate than just grabbing the first 5 items from a list
    const relatedGlossaryTerms = (() => {
      const relatedTerms: IRelatedGlossaryTerm[] = []
      const allChapters = Object.values(glossaryChapters.chapter) as {
        title: string;
        published: boolean;
      }[]

      if (!allChapters?.length) {
        return []
      }

      const currentIndex = allChapters.findIndex((chapter: any) =>
        chapter.title === chapterTest[0].title
      )

      const formatSlug = (title: string) => {
        return title
          .toLowerCase()
          .replace(/[\(\)]/g, ' ')
          .replace(/\s*\/\s*/g, '--')
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+$/, '')
      }

      // Return first 5 items if current chapter not found
      if (currentIndex === -1) {
        return allChapters.slice(0, 5).map((term: any) => ({
          title: term.title,
          slug: `/glossary/${formatSlug(term.title)}`
        }))
      }

      let nextIndex = (currentIndex + 1) % allChapters.length

      // First try to get next 5 items after current
      while (relatedTerms.length < 5 && nextIndex !== currentIndex) {
        const term = allChapters[nextIndex]
        relatedTerms.push({
          title: term.title,
          slug: `/glossary/${formatSlug(term.title)}`
        })
        nextIndex = (nextIndex + 1) % allChapters.length
      }

      // If we still need more items, wrap around from beginning
      if (relatedTerms.length < 5 && currentIndex > 0) {
        let index = 0
        while (relatedTerms.length < 5 && index < currentIndex) {
          const term = allChapters[index]
          relatedTerms.push({
            title: term.title,
            slug: `/glossary/${formatSlug(term.title)}`
          })
          index++
        }
      }

      return relatedTerms
    })()

    return {
      chapter: chapterTest,
      glossaryC,
      glossaryChapters,
      diagram: glossaryDiagrams[0],
      relatedGlossaryTerms,
    }
  },
  data() {
    return {
      thumbnailUrl: '',
      observer: null as any,
      autoSwitchTimer: 0 as any,
      featureSelected: 0,
      isPaused: true,
      featureStates: {},
      autoScrollActive: true,
      isDialogOpen: false,
      currentVideoUrl: '',
      trackSegmentEvent: null,
      iconSize: 28,
      btnMinHeight: 42,
      shareProviders: ['facebook', 'twitter', 'linkedin'],
      tooltipLikeText: 'Like',
      aspectRatio: '4 / 3',
      hasHistory: false,
      chapter: {
        title: '',
        content: '',
        video: {
          link: '',
          thumbnail: '',
        },
      },
      relatedGlossaryTerms: [],
      diagram: {
        name: '',
        fuid: 0,
        owner: {
          name: '',
          username: '',
        },
        link: '',
        forkCount: 0,
        likedByAuthenticatedUser: false,
        lastEdit: '',
        tags: [],
      },
    }
  },
  head() {
    // @ts-ignore
    const pageHead = this.pageHead()
    return {
      ...pageHead,
      // @ts-ignore
      title: `What is ${this.chapter[0].title}?`,
      meta: [
        {
          hid: 'og:description',
          name: 'og:description',
          // @ts-ignore
          content: this.chapter[0]?.metaDescription,
        },
        {
          hid: 'description',
          name: 'description',
          // @ts-ignore
          content: this.chapter[0]?.metaDescription,
        },
        ...pageHead?.meta,
      ],
    }
  },
  computed: {
    isAuthenticated(): boolean {
      return this.$store.getters.userIsAuthenticated
    },
    videoPoster(): string {
      const t = this.chapter[0].video?.thumbnail
      return typeof t === 'string' ? t : t?.url || ''
    },
  },
  mounted() {
    const glossaryChapters =
      this.$store.getters['wp/getAcfBlocks'][0].block.chapters[0]
    if (this.diagram) {
      this.setTooltipValue(
        // @ts-ignore
        this.diagram.likedByAuthenticatedUser
          ? `${this.$t('wordUnlikeCapital')}`
          : `${this.$t('wordLikeCapital')}`
      )
    }
  },
  methods: {
    toggleVideo() {
      this.isDialogOpen = true
    },
    handleVideoClick() {
      this.toggleVideo()
      // this.videoTracking()
    },
    getDateFromNow() {
      if (this.diagram)
        return this.$formatters.formatDateDiff(this.diagram.lastEdit)
      else return ''
    },
    setTooltipValue(value: string): void {
      this.tooltipLikeText = value
    },
    forkDiagramPressed(): void {
      if (this.isAuthenticated) {
        this.$store.dispatch(AppActions.TRACK_SEGMENT_EVENT, {
          context: this,
          params: {
            name: 'Glossary - Fork',
            category: 'Community',
            action: 'Fork',
            label: 'Glossary - Fork',
            misc: [
              {
                diagramName: this.diagram.name,
                diagramFuid: this.diagram.fuid,
                username: this.$store.$auth.$state.user.username,
              },
            ],
          },
        })
      }
    },
    diagramActionTSE(): void {
      if (this.isAuthenticated) {
        this.$store.dispatch(AppActions.TRACK_SEGMENT_EVENT, {
          context: this,
          params: {
            name: this.diagram.likedByAuthenticatedUser
              ? 'Glossary - unlike'
              : 'Glossary - like',
            category: 'Community',
            action: this.diagram.likedByAuthenticatedUser ? 'unlike' : 'like',
            label: this.diagram.likedByAuthenticatedUser
              ? 'Glossary unlike'
              : 'Glossary like',
            misc: [
              {
                diagramName: this.diagram.name,
                diagramFuid: this.diagram.fuid,
                username: this.$store.$auth.$state.user.username,
              },
            ],
          },
        })
      }
    },
  },
})
